import {
  Box,
  Button,
  Divider,
  Grid,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import FormDialog from "common/Modal/Modal";
import AddIcon from "@mui/icons-material/Add";
import { InventoryItemsTable } from "components/Inventory/InventoryItemsTable";
import { useSnackbar } from "notistack";
import { useGetItemsDropdownService } from "subModule/src/services/items/useItems";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { InventoryProfileService } from "subModule/src/core/services";
import {
  GetStockReceiptResponse,
  NewPurchaseOrderResponse,
  PurchaseOrderResponse,
  PurchaseOrderStatuses,
  SaveInvoice,
  SaveInvoiceResponse,
  SaveStockReceiptResponse,
} from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { DeleteData } from "subModule/src/type";
import dayjs from "dayjs";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
// import { ReactComponent as Completed } from "assets/completed.svg";
import { useGetPurchaseOrderStatusesService } from "subModule/src/services/inventory/useInventory";
import { GetItemsDropdownResponse } from "subModule/src/services/items/interface";
interface FormData {
  [key: string]: any;
}
interface StockReceivedProps {
  steps: string[];
  setCurrentSteps?: any;
}

const StockReceived: React.FC<StockReceivedProps> = ({
  steps,
  setCurrentSteps,
}) => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const supplierId = (location.state as { supplier?: number | null })?.supplier;
  const handleBack = () =>
    supplierId ? navigate(-1) : navigate("/inventory/purchase-orders");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    resetField,
    getValues,
  } = useForm<FormData>({
    defaultValues: {
      stockReceiptItems: [],
    },
  });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [po, setSR] = useState<PurchaseOrderResponse | SaveInvoice>();
  const { data: itemsData, refetch: itemsRefetch } = useGetItemsDropdownService(
    {
      pageNumber: dropdownPage,
      size: 20,
      storeId: po?.storeId || 0,
      search: dropdownSearchTerm,
    },
  );
  const { data: poStatusesData, refetch: poStatusesRefetch } =
    useGetPurchaseOrderStatusesService(+(params as any)?.id);
  const [currentStep, setCurrentStep] = useState<number>(
    steps.indexOf("Stock Received"),
  );
  const [confirm, setConfirm] = useState<boolean>(false);
  const [stockReceiptItems, setStockReceiptItems] = useState<any[]>([]);
  const [entity, setEntity] = useState<FormData>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [IsDataAvailable, setIsDataAvailable] = useState<boolean>(false);
  const [checkStatus, setCheckStatus] = useState<number | null>(1);
  const [allStatuses, setAllStatuses] = useState<PurchaseOrderStatuses>({});
  const [partsLoader, setPartsLoader] = useState<boolean>(true);
  useEffect(() => {
    setTimeout(() => setLoading(true), 10);
    poStatusesRefetch();
    if (params?.id) {
      getStockReceiptByPOId();
    }
  }, []);
  useEffect(() => {
    setAllStatuses(poStatusesData?.data || {});
  }, [poStatusesData]);
  useEffect(() => {
    if (po?.storeId) getParts();
  }, [po?.storeId && currentStep]);
  const getParts = async () => {
    await itemsRefetch()
      .then((res: GetItemsDropdownResponse | unknown | any) => {
        if (res?.data?.pages[0]?.success) {
          ///
        } else {
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => setPartsLoader(false));
  };
  const getStockReceiptByPOId = () => {
    InventoryProfileService.getStockReceiptByPOID(params?.id)
      .then((res: GetStockReceiptResponse) => {
        if (!res.data) {
          setIsDataAvailable(false);
          fetchInvoiceByPOID();
        } else {
          const allReceipts: any = res?.data || [];
          const mergedParts = allReceipts
            .flatMap((r: any) => r.stockReceiptItems || [])
            .reduce((acc: any[], item: any) => {
              const existing = acc.find((i) => i.itemId === item.itemId);
              if (existing) {
                existing.quantity = existing.quantity + item.quantity;
                existing.receivedDate = existing.receivedDate
                  ? null
                  : item.receivedDate?.split("T")[0];
              } else {
                acc.push({ ...item });
              }
              return acc;
            }, []);
          const mergedItems = {
            ...allReceipts[0],
            stockNumbers: allReceipts
              .map((r: any) => r.stockNumber)
              .filter(Boolean)
              .join(", "),
            receivedDates: allReceipts
              .map((r: any) => r.receivedDate?.split("T")[0])
              .filter(Boolean)
              .join(", "),
            totalReceived: allReceipts.reduce(
              (sum: number, r: any) => sum + (r.totalReceivedQuantity || 0),
              0,
            ),
            totalAmount: allReceipts.reduce(
              (sum: number, r: any) => sum + (r.totalAmount || 0),
              0,
            ),
            stockReceiptItems: mergedParts,
          };
          setIsDataAvailable(true);
          setSR(mergedItems);
          if ((res.data as any)[0]?.status === 2) setPartsLoader(false);
          setStockReceiptItems(mergedItems?.stockReceiptItems || []);
          setValue("stockReceiptItems", mergedItems?.stockReceiptItems || []);
          setValue("poId", params?.id);
          setValue("supplierId", (res.data as any)[0]?.supplierId);
          setValue("type", "stock");
          setCheckStatus((res.data as any)[0]?.status);
          setLoading(false);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  // const getPOById = () => {
  //     InventoryProfileService.getPurchaseOrderById(params?.id).then((res: NewPurchaseOrderResponse) => {
  //         if(res.success) {
  //             setSR(res.data);
  //             if((res.data as any)[0]?.status === 2) setPartsLoader(false);
  //             setStockReceiptItems(res.data.purchaseOrderItems.map(item => ({ ...item, receivedDate: dayjs(new Date()).format('YYYY-MM-DD') })));
  //             setValue('stockReceiptItems', res.data.purchaseOrderItems.map(item => ({ ...item, receivedDate: dayjs(new Date()).format('YYYY-MM-DD') })));
  //             setValue('storeId', res.data?.storeId);
  //             setValue('supplierId', res.data?.supplierId);
  //             setValue('poId', params?.id);
  //             setValue('type', 'stock');
  //             if(currentStep - 1 === steps.indexOf('supplierPayment')) fetchInvoiceByPOID();
  //             setLoading(false);
  //         } else {
  //             enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
  //             setLoading(false);
  //         }
  //     }).catch((err:any) => { console.error("===", err); setLoading(false); });
  // }
  const fetchInvoiceByPOID = () => {
    InventoryProfileService.getInvoiceByPOID(params?.id)
      .then((res: SaveInvoiceResponse) => {
        if (res.success) {
          setValue("purchaseInvoiceId", res?.data?.id || 0);
          setSR(res?.data || {});
          if (res?.data?.status === 2) setPartsLoader(false);
          setStockReceiptItems(
            (res?.data?.purchaseInvoiceItem || []).map((item) => ({
              ...item,
              receivedDate: dayjs(new Date()).format("YYYY-MM-DD"),
            })),
          );
          setValue(
            "stockReceiptItems",
            (res?.data?.purchaseInvoiceItem || []).map((item) => ({
              ...item,
              receivedDate: dayjs(new Date()).format("YYYY-MM-DD"),
            })),
          );
          setValue("storeId", res.data?.storeId);
          setValue("supplierId", res.data?.supplierId);
          setValue("poId", params?.id);
          setValue("type", "stock");
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      })
      .finally(() => setLoading(false));
  };
  const columns = [
    { id: "itemId" },
    { id: "itemCode" },
    { id: "unitId" },
    {
      id: "itemName",
      label: t("Item") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "supplierSKU",
      label: t("SKU"),
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    { id: "unitTitle" },
    {
      id: "unitPrice",
      label: t("Cost"),
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "discount",
      label: `${t("Discount")} %`,
      numeric: false,
      disablePadding: false,
      type: "number",
    },
    {
      id: "batchNumber",
      label: t("Batch"),
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    {
      id: "receivedDate",
      label: t("Received") + " " + t("Date"),
      numeric: false,
      disablePadding: false,
      type: "date",
    },
    {
      id: "expiryDate",
      label: t("Expiry") + " " + t("Date"),
      numeric: false,
      disablePadding: false,
      type: "date",
    },
    // { id: 'total', label: t('Total') },
  ];
  const DeleteItem = (row: any, typeName: string) => {
    if (typeName === "stockReceiptItems" && IsDataAvailable) {
      setLoading(true);
      InventoryProfileService.deleteEntityItem(
        (row as any).id,
        "stockreceipt/items",
      )
        .then((response: DeleteData) => {
          if (response.success)
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={t("deleted") + " " + t("successfully")}
              />,
              { variant: "success" },
            );
          else
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          setLoading(false);
          getStockReceiptByPOId();
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
          setLoading(false);
        });
    } else {
      setStockReceiptItems((prevRows) => {
        const newRows = prevRows.filter((rows) => rows.id !== row.id);
        setValue("stockReceiptItems", []);
        setTimeout(
          () =>
            setValue("stockReceiptItems", newRows, {
              shouldValidate: true,
              shouldDirty: true,
            }),
          0,
        );
        return newRows;
      });
    }
  };
  const addNewItem = (typeName: string) => {
    if (typeName === "stockReceiptItems") {
      const newItem = columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type === "number" ? 0 : "",
        }),
        {},
      );
      if (!IsDataAvailable) {
        (newItem as any).id = Date.now();
        (newItem as any).receivedDate = dayjs(new Date()).format("YYYY-MM-DD");
      }
      setStockReceiptItems((prevRows) => [...prevRows, newItem]);
    }
  };
  const handleItem = (row: any, typeName: string, action: string) => {
    if (typeName === "stockReceiptItems" && IsDataAvailable) {
      if (action === "create") {
        InventoryProfileService.createStockReceiptItem({
          ...row,
          stockReceiptId: po?.id,
          poId: params?.id,
          storeId: po?.storeId,
          expiryDate: row.expiryDate || null,
          receivedDate:
            row.receivedDate?.split(" ")[0] ||
            row.receivedDate ||
            dayjs(new Date())?.format("YYYY-MM-DD"),
        })
          .then((response: SaveStockReceiptResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={t("created") + " " + t("successfully")}
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getStockReceiptByPOId();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getStockReceiptByPOId();
          });
      } else {
        InventoryProfileService.updateStockReceiptItem({
          ...row,
          stockReceiptId: po?.id,
          poId: params?.id,
          receivedDate: row.receivedDate?.split(" ")[0] || row.receivedDate,
          expiryDate: row.expiryDate?.split(" ")[0] || row.expiryDate || null,
        })
          .then((response: SaveStockReceiptResponse) => {
            if (response.success)
              enqueueSnackbar(
                <AlertMessages
                  statusCode={response.status}
                  text={t("updated") + " " + t("successfully")}
                />,
                { variant: "success" },
              );
            else
              enqueueSnackbar(<AlertMessages text={response?.message} />, {
                variant: "error",
              });
            getStockReceiptByPOId();
          })
          .catch((err: any) => {
            console.error("===", err);
            enqueueSnackbar(
              <AlertMessages
                text={
                  err?.message || err?.data?.message || "Something went wrong"
                }
              />,
              { variant: "error" },
            );
            getStockReceiptByPOId();
          });
      }
    }
  };

  const handleInputChange = (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string,
  ) => {
    if (typeName === "stockReceiptItems") {
      const updatedItems = [...stockReceiptItems];
      updatedItems[index][columnId] =
        type === "number" ? parseFloat(value) : value;
      setStockReceiptItems(updatedItems);
    }
  };
  const onSubmit = (data: FormData) => {
    if (Object.values(data)?.length || Object.values(getValues())?.length) {
      if (checkStatus === 2 || checkStatus === 3 || checkStatus === 4) {
        if (currentStep + 1 === steps.indexOf("supplierBill")) {
          setCurrentStep(steps.indexOf("supplierBill"));
          setCurrentSteps(steps.indexOf("supplierBill"));
        }
        // else {
        //     setCurrentStep(steps.indexOf('Debit Note'));
        //     setCurrentSteps(steps.indexOf('Debit Note'));
        // }
      } else {
        const filteredItems = stockReceiptItems.filter(
          (item: any) => item?.itemName,
        );
        if (filteredItems.length === 0) {
          enqueueSnackbar(<AlertMessages text={t("addAtLeastOneItem")} />, {
            variant: "error",
          });
          return;
        }
        if (IsDataAvailable) {
          if (currentStep + 1 === steps.indexOf("supplierBill")) {
            setCurrentStep(steps.indexOf("supplierBill"));
            setCurrentSteps(steps.indexOf("supplierBill"));
            return;
          }
          setConfirm(true);
          setEntity(Object.values(data)?.length ? { ...data } : getValues());
        } else {
          setLoading(true);
          filteredItems.forEach((row: any) => {
            delete row.unitTitle;
            delete row.id;
          });
          InventoryProfileService.saveStockReceipt(
            Object.values(data)?.length
              ? {
                  ...data,
                  stockReceiptItems: filteredItems,
                }
              : { ...getValues(), stockReceiptItems: filteredItems },
          )
            .then((response: SaveStockReceiptResponse) => {
              if (response.success)
                enqueueSnackbar(
                  <AlertMessages
                    statusCode={response.status}
                    text={
                      t("Stock Received") +
                      " " +
                      t("saved") +
                      " " +
                      t("successfully")
                    }
                  />,
                  { variant: "success" },
                );
              else
                enqueueSnackbar(<AlertMessages text={response?.message} />, {
                  variant: "error",
                });
              getStockReceiptByPOId();
              if (currentStep + 1 === steps.indexOf("supplierBill")) {
                setCurrentStep(steps.indexOf("supplierBill"));
                setCurrentSteps(steps.indexOf("supplierBill"));
              }
              setLoading(false);
            })
            .catch((err: any) => {
              console.error("===", err);
              setLoading(false);
              enqueueSnackbar(
                <AlertMessages
                  text={
                    err?.message || err?.data?.message || "Something went wrong"
                  }
                />,
                { variant: "error" },
              );
            });
        }
      }
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      itemsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return loading ? (
    <Loader />
  ) : (
    <Grid
      container
      component="form"
      id="form"
      className="px-[8px] items-baseline"
      rowGap={2.5}
      columnGap={4}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container className="items-center ">
        <Grid item xs={12} sm={6} className="text-start">
          <Typography
            className="font-Saira text-[15px] font-semibold"
            style={{ color: themeColors?.primary }}
          >
            {IsDataAvailable ? t("Stock Received") : t("New Stock")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} className="hide-on-print">
          <div className="flex gap-2 justify-end">
            {currentStep === steps.length - 1 &&
            IsDataAvailable &&
            (checkStatus === 2 ||
              checkStatus === 3 ||
              checkStatus === 4) ? null : (
              <Button
                type="submit"
                className="primary"
                style={{ background: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => onSubmit(handleSubmit(onSubmit))}
              >
                {IsDataAvailable
                  ? checkStatus === 2 ||
                    checkStatus === 3 ||
                    checkStatus === 4 ||
                    (checkStatus === 1 &&
                      currentStep + 1 === steps.indexOf("supplierBill"))
                    ? t("Next")
                    : t("Confirm")
                  : t("Save as received")}
              </Button>
            )}
            {/*  <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('email')}</Button> */}
            {checkStatus === 2 || checkStatus === 3 || checkStatus === 4 ? (
              <Button
                className="secondary"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() =>
                  navigate(`${window.location.pathname}/print/stock`)
                }
              >
                {t("Print")}
              </Button>
            ) : null}
            <Button
              className="secondary"
              style={{ color: themeColors?.primary }}
              variant="contained"
              disableElevation
              onClick={handleBack}
            >
              {t("Back to list")}
            </Button>
          </div>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider className="bg-[#AEB3C4] hide-on-print" />
      </Grid>
      <Grid
        container
        className="py-[12px] px-[18px] rounded-[8px] mb-[16px] items-center"
        style={{ background: themeColors?.primary }}
      >
        <Grid item xs={4}>
          <Typography className="font-Saira text-[15px] font-bold text-[#fff] text-left">
            {IsDataAvailable ? t("Stock Received") : t("New Stock")}
          </Typography>
          <Typography className="font-Saira text-[13px] text-[#fff] text-left">
            <span className="font-bold">
              {IsDataAvailable ? t("Stock") + " #" : t("Purchase Order") + " #"}
            </span>{" "}
            <span>{po?.poNumber || (po as any)?.stockNumbers || ""}</span>
          </Typography>
          {!IsDataAvailable ? (
            <Typography className="font-Saira text-[13px] text-[#fff] text-left">
              <span className="font-bold">{t("Date")}</span>{" "}
              <span>{po?.poDate?.split("T")[0] || ""}</span>
            </Typography>
          ) : (
            <Typography className="font-Saira text-[13px] text-[#fff] text-left">
              <span className="font-bold">{t("Date")}</span>{" "}
              <span>{(po as any)?.receivedDates || ""}</span>
            </Typography>
          )}
        </Grid>
        <Grid item xs={8}>
          <div className="flex justify-end gap-10">
            <Typography className="font-Saira text-[11px] text-[#fff] text-center">
              <span className="font-bold">
                {t("Supplier")} {t("name")}
              </span>{" "}
              <br />
              <span className="font-medium">{po?.vendorTitle || ""}</span>
            </Typography>
            <Typography className="font-Saira text-[11px] text-[#fff] text-center">
              <span className="font-bold">{t("store") + " " + t("name")}</span>{" "}
              <br />
              <span className="font-medium">{po?.storeTitle || ""}</span>
            </Typography>
            <Typography className="font-Saira text-[11px] text-[#fff] text-center">
              <span className="font-bold">{t("Requested")}</span> <br />
              <span className="font-medium">{po?.totalQuantity || 0}</span>
            </Typography>
            <Typography className="font-Saira text-[11px] text-[#fff] text-center">
              <span className="font-bold">{t("Received")}</span> <br />
              <span className="font-medium">
                {(po as any)?.totalReceived || 0}
              </span>
            </Typography>
            {!IsDataAvailable ? (
              <>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Subtotal")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (po as PurchaseOrderResponse)?.subtotalAmount || 0,
                    ) || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Discount")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (po as PurchaseOrderResponse)?.discount || 0,
                    ) || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("tax")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (po as PurchaseOrderResponse)?.taxAmount || 0,
                    ) || 0}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("Total")}</span> <br />
                  <span className="font-medium">
                    {new Intl.NumberFormat("en-US", {}).format(
                      (po as PurchaseOrderResponse)?.totalAmount || 0,
                    ) || 0}
                  </span>
                </Typography>
              </>
            ) : (
              <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                <span className="font-bold">{t("Amount")}</span> <br />
                <span className="font-medium">
                  {new Intl.NumberFormat("en-US", {}).format(
                    (po as any)?.totalAmount || 0,
                  ) || 0}
                </span>
              </Typography>
            )}
          </div>
        </Grid>
      </Grid>
      <Grid container className="mt-2">
        <Grid item xs={12}>
          {partsLoader ? (
            <Box sx={{ "& .MuiBackdrop-root": { zIndex: 999 } }}>
              <Loader />
            </Box>
          ) : null}
          <NewPurchaseSteps
            form={{
              control,
              register,
              handleSubmit,
              setValue,
              errors,
              watch,
              resetField,
            }}
            addNewItem={addNewItem}
            handleInputChange={handleInputChange}
            items={itemsData?.pages[0]?.data?.content || []}
            DeleteItem={DeleteItem}
            handleItem={handleItem}
            checkStatus={checkStatus}
            {...{
              currentStep,
              setCurrentStep,
              columns,
              stockReceiptItems,
              loggedInUserCurrency,
              params,
              IsDataAvailable,
              steps,
              setCurrentSteps,
              t,
              themeColors,
              allStatuses,
              dropdownPage,
              searchLoading,
              dropdownSearchTerm,
              setDropdownSearchTerm,
              setDropdownPage,
            }}
            itemsData={itemsData || {}}
            handleDropdownActions={handleDropdownActions}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} className="hide-on-print">
        <Divider className="bg-[#AEB3C4] mt-[8px]" />
      </Grid>
      <Grid container className="items-center hide-on-print">
        <Grid item xs={12} sm={6} className="text-start"></Grid>
        <Grid item xs={12} sm={6}>
          <div className="flex gap-2 justify-end">
            {currentStep === steps.length - 1 &&
            IsDataAvailable &&
            (checkStatus === 2 ||
              checkStatus === 3 ||
              checkStatus === 4) ? null : (
              <Button
                type="submit"
                className="primary"
                style={{ background: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() => onSubmit(handleSubmit(onSubmit))}
              >
                {IsDataAvailable
                  ? checkStatus === 2 ||
                    checkStatus === 3 ||
                    checkStatus === 4 ||
                    (checkStatus === 1 &&
                      currentStep + 1 === steps.indexOf("supplierBill"))
                    ? t("Next")
                    : t("Confirm")
                  : t("Save as received")}
              </Button>
            )}
            {/*  <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('email')}</Button> */}
            {checkStatus === 2 || checkStatus === 3 || checkStatus === 4 ? (
              <Button
                className="secondary"
                style={{ color: themeColors?.primary }}
                variant="contained"
                disableElevation
                onClick={() =>
                  navigate(`${window.location.pathname}/print/stock`)
                }
              >
                {t("Print")}
              </Button>
            ) : null}
            <Button
              className="secondary"
              style={{ color: themeColors?.primary }}
              variant="contained"
              disableElevation
              onClick={handleBack}
            >
              {t("Back to list")}
            </Button>
          </div>
        </Grid>
      </Grid>
      {confirm && (
        <FormDialog
          open={confirm}
          onClose={(event, reason) =>
            event && reason !== "backdropClick" ? setConfirm(false) : ""
          }
          title={t("Confirm") + " " + t(steps[currentStep])}
          saveFunction={() => {
            delete entity.typeName;
            entity.stockReceiptItems.every((row: any) => delete row.total);
            setConfirm(false);
            InventoryProfileService.changeStatusEntity({
              typeName: "stockreceipt",
              entityId: po?.id,
              status: 2,
            })
              .then((response: NewPurchaseOrderResponse) => {
                if (response.success) {
                  setConfirm(false);
                  if (currentStep + 1 === steps.indexOf("supplierBill")) {
                    setCurrentStep(steps.indexOf("supplierBill"));
                    setCurrentSteps(steps.indexOf("supplierBill"));
                  }
                  // else {
                  //     setCurrentStep(steps.indexOf('Debit Note'));
                  //     setCurrentSteps(steps.indexOf('Debit Note'));
                  // }
                  getStockReceiptByPOId();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("Status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  setConfirm(false);
                  enqueueSnackbar(<AlertMessages text={response?.message} />, {
                    variant: "error",
                  });
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                setConfirm(false);
                enqueueSnackbar(
                  <AlertMessages
                    text={
                      err?.message ||
                      err?.data?.message ||
                      "Something went wrong"
                    }
                  />,
                  { variant: "error" },
                );
              });
          }}
          content={
            <>
              <Typography>{t("areYouSureUneditable")}</Typography>
            </>
          }
          submitButtonText="OK"
        />
      )}
    </Grid>
  );
};

interface NewPurchaseStepsProps {
  form: FormData;
  addNewItem: (typeName: string) => void;
  handleInputChange: (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string,
  ) => void;
  currentStep: number;
  setCurrentStep: any;
  setCurrentSteps: any;
  columns: any[];
  stockReceiptItems: any[];
  items: any[];
  loggedInUserCurrency?: string;
  DeleteItem: (row: any, typeName: string) => void;
  params?: any;
  handleItem?: (row: any, typeName: string, action: string) => void;
  IsDataAvailable?: boolean;
  steps: string[];
  t?: any;
  themeColors?: any;
  allStatuses?: PurchaseOrderStatuses;
  checkStatus?: number | null;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  dropdownPage: number;
  searchLoading: string | null;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  setDropdownPage: any;
  itemsData: any;
}

export const NewPurchaseSteps: React.FC<NewPurchaseStepsProps> = ({
  form,
  addNewItem,
  handleInputChange,
  currentStep,
  columns,
  stockReceiptItems,
  items,
  loggedInUserCurrency,
  DeleteItem,
  params,
  handleItem,
  IsDataAvailable,
  steps,
  setCurrentStep,
  setCurrentSteps,
  t,
  themeColors,
  allStatuses,
  checkStatus,
  handleDropdownActions,
  itemsData,
  dropdownPage,
  searchLoading,
  dropdownSearchTerm,
  setDropdownSearchTerm,
  setDropdownPage,
}) => {
  return (
    <>
      <Stepper
        className="hide-on-print"
        activeStep={currentStep}
        connector={<></>}
        sx={{
          "& .MuiStep-horizontal .Mui-active + .MuiStepLabel-labelContainer": {
            background: `linear-gradient(20.78deg, ${themeColors?.secondary}, ${themeColors?.secondary + "B3"})`,
          },
        }}
      >
        {steps.map((label, index) => {
          const statusKeyMap: Record<string, keyof PurchaseOrderStatuses> = {
            "Purchase Order": "purchaseOrder",
            supplierBill: "purchaseInvoice",
            "Stock Received": "stockReceive",
            "Debit Note": "purchaseCreditDebitNote",
            Unstock: "unStock",
          };
          const stepProps: { completed?: boolean } = {};
          const labelProps: { optional?: React.ReactNode } = {};
          const key = statusKeyMap[label];
          const isCompleted =
            allStatuses?.[key] === 2 ||
            allStatuses?.[key] === 3 ||
            allStatuses?.[key] === 4;
          const isSupplierBillCompleted =
            allStatuses?.purchaseInvoice === 2 ||
            allStatuses?.purchaseInvoice === 3 ||
            allStatuses?.purchaseInvoice === 4;
          return (
            <Step
              key={index}
              completed={isCompleted}
              {...stepProps}
              onClick={() => {
                if (
                  document
                    .querySelector(`.MuiStep-root:nth-child(${index + 1})`)
                    ?.classList.contains("Mui-completed") ||
                  (isSupplierBillCompleted && label === "supplierPayment")
                ) {
                  setCurrentStep(steps.indexOf(label));
                  setCurrentSteps(steps.indexOf(label));
                }
              }}
              className={`flex items-center ${document.querySelector(`.MuiStep-root:nth-child(${index + 1})`)?.classList.contains("Mui-completed") || (steps.indexOf("supplierPayment") && currentStep - 1 === steps.indexOf("supplierPayment")) ? "cursor-pointer" : ""}`}
            >
              {/* {document.querySelector(`.MuiStep-root:nth-child(${index + 1})`)?.classList.contains('Mui-completed') || (steps.indexOf('supplierPayment') && currentStep - 1 === steps.indexOf('supplierPayment')) ? <Completed style={{ fill: themeColors?.primary, width: '15px', height: '15px' }} /> : null} */}
              <StepLabel {...labelProps}>{t(label)}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box className="bg-white py-[12px] px-[18px] rounded-[8px] mt-[16px]">
        <Typography className="font-Saira text-[11px] font-semibold text-[#171826] text-left">
          {t(steps[currentStep])}
        </Typography>
        <InventoryItemsTable
          form={form}
          columns={columns}
          handleInputChange={handleInputChange}
          rows={stockReceiptItems}
          typeName="stockReceiptItems"
          items={items}
          currency={loggedInUserCurrency}
          DeleteItem={DeleteItem}
          handleItem={handleItem}
          {...{
            params,
            dropdownPage,
            searchLoading,
            dropdownSearchTerm,
            setDropdownSearchTerm,
            setDropdownPage,
          }}
          checkStatus={checkStatus || 1}
          viewDelete={IsDataAvailable ? false : true}
          itemsResponse={itemsData || {}}
          handleDropdownActions={handleDropdownActions}
        />
        <Grid item xs={12} sm={6} className="hide-on-print">
          <div className="flex gap-2 justify-start">
            <Button
              className="secondary flex gap-1"
              style={{ color: themeColors?.primary }}
              variant="contained"
              disableElevation
              onClick={() => addNewItem("stockReceiptItems")}
            >
              <AddIcon className="w-[20px] h-[20px]" /> {t("Add More Items")}
            </Button>
            {/* <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('Import')}</Button>
                    <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t("Export")}</Button> */}
          </div>
        </Grid>
      </Box>
    </>
  );
};

export default StockReceived;
